import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { PageBreadcrumbComponent } from '../../../../../shared/components/common/page-breadcrumb/page-breadcrumb.component';
import { ConfirmDialogService } from '../../../../../shared/services/confirm-dialog.service';
import { ToastService } from '../../../../../shared/services/toast.service';
import { HasPermissionDirective } from '../../../../../shared/directives/has-permission.directive';
import { TicketsConfigApiService } from '../../../services/tickets-config-api.service';
import { TicketsCategoryDrawerService } from '../../../services/tickets-config-drawer.services';
import { TicketCategory } from '../../../models/tickets-config.model';

interface CategoryRow extends TicketCategory {
  depth: number;
  parentName: string;
}

@Component({
  selector: 'app-tickets-categories-list',
  standalone: true,
  imports: [CommonModule, FormsModule, PageBreadcrumbComponent, HasPermissionDirective],
  templateUrl: './categories-list.component.html'
})
export class TicketsCategoriesListComponent implements OnInit, OnDestroy {
  items: TicketCategory[] = [];
  rows: CategoryRow[] = [];
  filtered: CategoryRow[] = [];
  loading = false;
  error = '';

  filtroNombre = '';
  filtroSoloActivas = false;

  private readonly destroy$ = new Subject<void>();

  constructor(
    protected router: Router,
    private configApi: TicketsConfigApiService,
    private drawer: TicketsCategoryDrawerService,
    private confirmDialog: ConfirmDialogService,
    private toast: ToastService
  ) {}

  ngOnInit(): void {
    this.cargar();
    this.drawer.entitySaved$.pipe(takeUntil(this.destroy$)).subscribe(() => this.cargar());
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  cargar(): void {
    this.loading = true;
    this.error = '';
    this.configApi.getCategories().pipe(takeUntil(this.destroy$)).subscribe({
      next: (data) => {
        this.items = data;
        this.rows = this.buildHierarchy(data);
        this.aplicarFiltros();
        this.loading = false;
      },
      error: (err) => {
        this.error = err.error?.message || 'Error al cargar categorías';
        this.loading = false;
      }
    });
  }

  /**
   * Aplana el árbol jerárquico ordenado por sortOrder dentro de cada nivel,
   * agregando depth para indentación visual.
   */
  private buildHierarchy(items: TicketCategory[]): CategoryRow[] {
    const byParent = new Map<number | null, TicketCategory[]>();
    for (const it of items) {
      const key = it.idParent ?? null;
      if (!byParent.has(key)) byParent.set(key, []);
      byParent.get(key)!.push(it);
    }
    for (const list of byParent.values()) {
      list.sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));
    }
    const nameById = new Map<number, string>();
    items.forEach((i) => nameById.set(i.id, i.name));

    const out: CategoryRow[] = [];
    const dfs = (parentId: number | null, depth: number) => {
      const children = byParent.get(parentId) ?? [];
      for (const c of children) {
        out.push({
          ...c,
          depth,
          parentName: c.idParent != null ? nameById.get(c.idParent) ?? '' : ''
        });
        dfs(c.id, depth + 1);
      }
    };
    dfs(null, 0);
    return out;
  }

  aplicarFiltros(): void {
    const term = this.filtroNombre.trim().toLowerCase();
    this.filtered = this.rows.filter((r) => {
      if (term && !r.name.toLowerCase().includes(term)) return false;
      if (this.filtroSoloActivas && !r.isActive) return false;
      return true;
    });
  }

  nuevo(): void {
    this.drawer.open();
  }

  editar(item: TicketCategory): void {
    this.drawer.open({ id: item.id });
  }

  toggleActiva(item: TicketCategory): void {
    this.configApi
      .updateCategory(item.id, {
        name: item.name,
        description: item.description ?? undefined,
        color: item.color ?? undefined,
        idParent: item.idParent ?? undefined,
        sortOrder: item.sortOrder,
        isActive: !item.isActive
      })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => this.cargar(),
        error: (err) => {
          this.toast.error({
            title: 'Error',
            message: err.error?.message || 'No se pudo actualizar la categoría'
          });
        }
      });
  }

  async eliminar(item: TicketCategory): Promise<void> {
    const ok = await this.confirmDialog.confirmDanger({
      title: 'Eliminar categoría',
      message: `¿Eliminar la categoría "${item.name}"?`
    });
    if (!ok) return;
    this.configApi.deleteCategory(item.id).pipe(takeUntil(this.destroy$)).subscribe({
      next: () => {
        this.toast.deleted('Categoría', `Se eliminó "${item.name}" correctamente.`);
        this.cargar();
      },
      error: (err) => {
        this.toast.error({
          title: 'Error',
          message: err.error?.message || 'No se pudo eliminar la categoría'
        });
      }
    });
  }

  volverATickets(): void {
    this.router.navigate(['/tickets']);
  }

  indentChars(depth: number): string {
    return '— '.repeat(depth);
  }
}
